import { createRouter, createWebHistory } from 'vue-router';
import HomePage from '@/views/HomePage.vue';
import PrivatePage from '@/views/PrivatePage.vue';
import RiceGamePage from '@/views/RiceGamePage.vue';
import TelegramUploadReceiptPage from '@/views/TelegramUploadReceiptPage.vue';
import NotFound from '@/views/NotFound.vue';
import { getToken, setToken, setAuthHeader } from '@/api';
import { getMainSettings } from '@/api'; // Импортируем ваш метод
import adRiver from '@/services/adriver';
import { metrikaHit } from '@/utils/metrika';
import { trackAllSitePixels } from '@/utils/pixels';
// import { sendAdRiverMetrik } from '@/utils/metrika';

const SEO_TITLE = '«Первое правило плова» — акция на rice.ru';
const SEO_DESCRIPTION =
  'Акция «Первое правило плова» проходит с 1 мая по 12 июня 2026 года. Регистрируйте чеки и участвуйте в розыгрышах призов на сайте rice.ru.';

/** Не подменять title/description из API, если в админке ещё стоят заглушки (ломает вкладку и сниппеты). */
function isSeoMetaPlaceholder(value) {
  const raw = value == null ? '' : String(value).trim();
  if (!raw) return true;
  const lower = raw.toLowerCase();
  if (lower === 'title') return true;
  if (lower === 'description') return true;
  if (lower.startsWith('descript')) return true;
  return false;
}

const routes = [
  { path: '/', component: HomePage, meta: { title: SEO_TITLE, description: SEO_DESCRIPTION } },
  {
    path: '/telegram_bot_about_promo_page',
    component: HomePage,
    meta: { title: SEO_TITLE, description: SEO_DESCRIPTION, isTelegramPage: true, telegramPageType: 'about' },
  },
  {
    path: '/telegram_bot_winners_page',
    component: HomePage,
    meta: { title: SEO_TITLE, description: SEO_DESCRIPTION, isTelegramPage: true, telegramPageType: 'winners' },
  },
  {
    path: '/private',
    component: PrivatePage,
    meta: { requiresAuth: true, title: SEO_TITLE, description: SEO_DESCRIPTION },
  },
  {
    path: '/private/game/plov',
    redirect: (to) => {
      const slotN = parseInt(to.query.slot, 10);
      const slot = Number.isFinite(slotN) && slotN >= 1 && slotN <= 6 ? slotN : 1;
      const ph = String(to.query.phase || '').toLowerCase();
      const phase = ['intro', 'play', 'result'].includes(ph) ? ph : 'intro';
      const query = { ...to.query };
      delete query.slot;
      delete query.phase;
      return { path: `/private/game/plov/${slot}/${phase}`, query };
    },
  },
  {
    path: '/private/game/plov/:slot/:phase',
    component: RiceGamePage,
    meta: { requiresAuth: true, title: SEO_TITLE, description: SEO_DESCRIPTION },
  },
  {
    path: '/telegram_bot_profile_page',
    component: PrivatePage,
    meta: { requiresAuth: true, title: SEO_TITLE, description: SEO_DESCRIPTION, isTelegramPage: true, telegramPageType: 'profile' },
  },
  {
    path: '/telegram_bot_prizes_page',
    component: PrivatePage,
    meta: { requiresAuth: true, title: SEO_TITLE, description: SEO_DESCRIPTION, isTelegramPage: true, telegramPageType: 'prizes' },
  },
  {
    path: '/telegram_bot_upload_receipt_page',
    component: TelegramUploadReceiptPage,
    meta: { requiresAuth: true, title: SEO_TITLE, description: SEO_DESCRIPTION, isTelegramPage: true, telegramPageType: 'upload' },
  },
  { path: '/:catchAll(.*)', component: NotFound },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  const queryToken = typeof to.query?.token === 'string' ? to.query.token.trim() : '';
  if (queryToken) {
    setToken(queryToken);
    setAuthHeader();
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const isAuthenticated = !!getToken();

  if (requiresAuth && !isAuthenticated) {
    next('/');
  } else {
    next();
  }
});

let metrikaInitialNavigationDone = false;

router.afterEach(async (to, from) => {
  // Загружаем главные настройки с API
  const metaInfo = to.meta;
  if (metaInfo) {

    let title = metaInfo.title || SEO_TITLE;
    let description = metaInfo.description || SEO_DESCRIPTION;

    // Получаем настройки с API только для маршрутов, где это нужно.
    const needsMainSettings = to.path === '/' || to.path === '/private';
    if (needsMainSettings) {
      try {
        const settings = await getMainSettings();
        if (settings?.title && !isSeoMetaPlaceholder(settings.title)) {
          title = settings.title;
        }
        if (settings?.description && !isSeoMetaPlaceholder(settings.description)) {
          description = settings.description;
        }
      } catch (error) {
        // Не блокируем навигацию из-за сетевой ошибки мета-настроек.
      }
    }

    document.title = title;

    // Обновляем мета-теги
    const descriptionMetaTag = document.querySelector('meta[name="description"]');
    const ogDescriptionMetaTag = document.querySelector('meta[property="og:description"]');
    const twitterDescriptionMetaTag = document.querySelector('meta[name="twitter:description"]');

    const ogTitle = document.querySelector('meta[property="og:title"]');
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');

    if (ogTitle && twitterTitle) {
      ogTitle.setAttribute('content', title);
      twitterTitle.setAttribute('content', title);
    }
    if (descriptionMetaTag && ogDescriptionMetaTag && twitterDescriptionMetaTag) {
      descriptionMetaTag.setAttribute('content', description);
      ogDescriptionMetaTag.setAttribute('content', description);
      twitterDescriptionMetaTag.setAttribute('content', description);
    }
  }

  if (to.path !== from.path) {
    adRiver.trackPageView();
  }

  trackAllSitePixels();

  if (metrikaInitialNavigationDone && to.fullPath !== from.fullPath) {
    metrikaHit(typeof window !== 'undefined' ? window.location.href : to.fullPath);
  }
  metrikaInitialNavigationDone = true;
});

export default router;
